import React from 'react'
import PropTypes from 'prop-types'
import get from 'lodash.get'
import {
  convertSlateToDelta,
  convertS2ToDelta,
  convertDeltaToSlate,
  convertDeltaToS2,
  getType,
  getDataField,
  isKeyCodePrintable
} from '@sukiai/utils'

import { isAmwellTheme } from '@sukiai/components/lib/util'

import {
  PERSONA,
  KEY,
  ERROR_CONTENT,
  FORMATS,
  INLINE_TYPE,
  V2_DEFAULT_CONTENT,
  EDITOR_TYPE,
  KEY_CODE,
  DYNAMIC_CHIP_MAPPING,
  NODE_TYPES
} from './constants'
import './styles.css'
import ReactQuill from 'react-quill'
import 'quill-mention'
import unidecode from 'unidecode'
import isNil from 'lodash/isNil'
import isEmpty from 'lodash/isEmpty'

const Quill = ReactQuill.Quill
const Delta = Quill.import('delta')

// Adds the 'type' attribute to Delta since it is not natively supported by Quill
let Parchment = Quill.import('parchment')
const typeInlineAttributor = new Parchment.Attributor.Attribute('type', 'data-type', {
  scope: Parchment.Scope.INLINE
})

// This will be used for S2 <-> Delta
const typeBlockAttributor = new Parchment.Attributor.Attribute('blockType', 'data-block-type', {
  scope: Parchment.Scope.BLOCK
})

Quill.register(typeInlineAttributor)
Quill.register(typeBlockAttributor)

// Unfortunately there is no Quill API that will return a Quill.
// so had to adabt this solution
const __editors = {}
const __underscoreOccurances = {}

const getQuillInstance = (quillID) => {
  return __editors[quillID]
}

const getUnderscoreOccurances = (quillID) => {
  return __underscoreOccurances[quillID]
}

const getAllIndex = (string) => {
  const regex = /_{2,}/gi
  const indexPairs = []
  let matchArr = []
  while ((matchArr = regex.exec(string)) !== null) { indexPairs.push([matchArr.index, regex.lastIndex]) }
  return indexPairs
}

// TODO: commenting new chips as part of release 13.0
const plusValues = [
  { id: 2, value: DYNAMIC_CHIP_MAPPING.BLOOD_PRESSURE },
  { id: 3, value: DYNAMIC_CHIP_MAPPING.HEART_RATE },
  { id: 4, value: DYNAMIC_CHIP_MAPPING.TEMPERATURE },
  { id: 5, value: DYNAMIC_CHIP_MAPPING.RESPIRATORY_RATE },
  { id: 6, value: DYNAMIC_CHIP_MAPPING.PULSE_OXIMETRY },
  { id: 7, value: DYNAMIC_CHIP_MAPPING.HEIGHT },
  { id: 8, value: DYNAMIC_CHIP_MAPPING.WEIGHT },
  { id: 9, value: DYNAMIC_CHIP_MAPPING.BMI },
  { id: 10, value: DYNAMIC_CHIP_MAPPING.PAST_MEDICAL_HISTORY },
  { id: 11, value: DYNAMIC_CHIP_MAPPING.PAST_SURGICAL_HISTORY },
  { id: 12, value: DYNAMIC_CHIP_MAPPING.ALLERGIES },
  { id: 13, value: DYNAMIC_CHIP_MAPPING.CURRENT_MEDICATION },
  { id: 100, value: DYNAMIC_CHIP_MAPPING.FIRST_NAME },
  { id: 101, value: DYNAMIC_CHIP_MAPPING.LAST_NAME },
  { id: 102, value: DYNAMIC_CHIP_MAPPING.GENDER },
  { id: 103, value: DYNAMIC_CHIP_MAPPING.AGE },
  { id: 104, value: DYNAMIC_CHIP_MAPPING.CHIEF_COMPLAINT },
];

const adminOnlyPlusValues = [
  { id: 14, value: DYNAMIC_CHIP_MAPPING.LEUKOCYTES },
  { id: 15, value: DYNAMIC_CHIP_MAPPING.ERYTHROCYTES },
  { id: 16, value: DYNAMIC_CHIP_MAPPING.HEMOGLOBIN },
  { id: 17, value: DYNAMIC_CHIP_MAPPING.HEMATOCRIT },
  { id: 18, value: DYNAMIC_CHIP_MAPPING.PLATELETS },
  { id: 19, value: DYNAMIC_CHIP_MAPPING.GLUCOSE },
  { id: 20, value: DYNAMIC_CHIP_MAPPING.UREA },
  { id: 21, value: DYNAMIC_CHIP_MAPPING.CREATININE },
  { id: 22, value: DYNAMIC_CHIP_MAPPING.SODIUM },
  { id: 23, value: DYNAMIC_CHIP_MAPPING.POTASSIUM },
  { id: 24, value: DYNAMIC_CHIP_MAPPING.CHLORIDE },
  { id: 25, value: DYNAMIC_CHIP_MAPPING.CARBON_DIOXIDE },
  { id: 26, value: DYNAMIC_CHIP_MAPPING.PROTEIN },
  { id: 27, value: DYNAMIC_CHIP_MAPPING.ALBUMIN },
  { id: 28, value: DYNAMIC_CHIP_MAPPING.BILIRUBIN_TOTAL },
  { id: 29, value: DYNAMIC_CHIP_MAPPING.BILIRUBIN_DIRECT },
  { id: 30, value: DYNAMIC_CHIP_MAPPING.ALKALINE_PHOSPHATASE },
  { id: 31, value: DYNAMIC_CHIP_MAPPING.ASPARTATE_AMINOTRANSFERASE },
  { id: 32, value: DYNAMIC_CHIP_MAPPING.THYROXINE },
  { id: 33, value: DYNAMIC_CHIP_MAPPING.THYROTROPIN },
  { id: 34, value: DYNAMIC_CHIP_MAPPING.TOTAL_CHOLESTEROL },
  { id: 35, value: DYNAMIC_CHIP_MAPPING.CHOLESTEROL_HDL },
  { id: 36, value: DYNAMIC_CHIP_MAPPING.CHOLESTEROL_LDL },
  { id: 37, value: DYNAMIC_CHIP_MAPPING.TRIGLYCERIDES },
  { id: 38, value: DYNAMIC_CHIP_MAPPING.PROSTATE_ANTIGEN },
  { id: 39, value: DYNAMIC_CHIP_MAPPING.HEMOGLOBIN_A1C },
  { id: 40, value: DYNAMIC_CHIP_MAPPING.INR },
  { id: 41, value: DYNAMIC_CHIP_MAPPING.ALANINE_AMINOTRANSFERASE },
  { id: 42, value: DYNAMIC_CHIP_MAPPING.XRAY_NARRATIVE },
  { id: 43, value: DYNAMIC_CHIP_MAPPING.MRI_NARRATIVE },
  { id: 44, value: DYNAMIC_CHIP_MAPPING.CT_NARRATIVE },
  { id: 99, value: DYNAMIC_CHIP_MAPPING.PAST_MEDICAL_HISTORY_SHOW_ME },
  { id: 100, value: DYNAMIC_CHIP_MAPPING.PAST_SURGICAL_HISTORY_SHOW_ME },
]

let didPaste = false

class QuillEditor extends React.Component {
  static propTypes = {
    id: PropTypes.string,
    value: PropTypes.string,
    defaultValue: PropTypes.string,
    persona: PropTypes.oneOf(Object.values(PERSONA)).isRequired,
    handleChange: PropTypes.func,
    enablePunct: PropTypes.bool,
    focused: PropTypes.bool,
    readOnly: PropTypes.bool,
    isScriptInsert: PropTypes.bool
  }

  constructor(props) {
    super(props)

    this.quillRef = null
    this.reactQuillRef = null
    this.scripts = props.scripts

    if (props.defaultValue) {
      try {
        const defaultQuillVal = props.s2Mode
          ? convertS2ToDelta(props.defaultValue)
          : convertSlateToDelta(props.defaultValue, props.persona, props.editorType)

        const metadata = props.s2Mode
          ? null
          : getDataField(props.defaultValue)
        this.state = { defaultQuillVal, metadata, error: null, isTabKey: false, isShiftKey: false, currentUnderscorePositions: null, isScriptInsert: false }
      } catch (error) {
        console.error('Unable to parse default Slate', props.defaultValue, error)
        this.state = {
          defaultQuillVal: ERROR_CONTENT,
          error: 'Unable to parse default Slate'
        }
      }
    } else {
      try {
        const quillVal = props.s2Mode
          ? convertS2ToDelta(props.value)
          : convertSlateToDelta(props.value, props.persona, props.editorType)

        const metadata = props.s2Mode
          ? null
          : getDataField(props.value)

        this.state = { quillVal, metadata, error: null, isTabKey: false, isShiftKey: false, currentUnderscorePositions: null, isScriptInsert: false  }
      } catch (error) {
        console.error('unable to parse Slate value', props.value, error)
        this.state = {
          defaultQuillVal: ERROR_CONTENT,
          error: 'Unable to parse Slate value'
        }
      }
    }
  }

  componentDidMount() {
    const {
      cursorPosition,
      handleBlur,
      handleFocus,
      handleCursorChange,
      persona,
      editorType,
    } = this.props
    const { quillVal } = this.state

    this.attachQuillRefs()

    // Clears the history so that you can't ctrl+z to an empty editor
    this.quillRef.history.clear()

    if (persona === PERSONA.PHYSICIAN && editorType !== 'SCRIPT' && editorType !== 'NOTETYPE') {
      this.quillRef.on('selection-change', async (range, oldRange, source) => {
        if (!oldRange && source === 'user') {
          return
        }

        if (this.state.readOnly) {
          return
        }

        if (range === null && oldRange !== null) {
          handleBlur()
        } else if (range !== null && oldRange === null) {
          await handleFocus()
        }

        if (range) {
          handleCursorChange(range, source)
        }
      })
    }

    // Set contents of editor in Admin note view and place green dot at the current cursor position
    if (this.props.readOnly && cursorPosition !== undefined && persona === PERSONA.ADMIN) {
      this.quillRef.setContents(quillVal)
      this.quillRef.insertText(cursorPosition, '●', { background: 'none', type: 'UNKNOWN', color: 'green' })
    }
  }

  attachQuillRefs = () => {
    if (typeof this.reactQuillRef.getEditor !== 'function') return
    this.quillRef = this.reactQuillRef.getEditor()

    // Unfortunately there is no Quill API that will return a Quill.
    __editors[this.props.id] = this.quillRef

    const sectionText = this.quillRef.getText()

    // returns all start and end index positions of underscores in a section
    const underScoresArr = getAllIndex(sectionText)
    __underscoreOccurances[this.props.id] = underScoresArr
  }

  // Check if quill section is empty
  isQuillEmpty = (quill) => {
    return quill.getText().trim().length === 0
  }

  getNextUnderscorePosition = (id, isShiftKey, inNextSection = false) => {
    // get current cursor position
    let currentPosition = this.quillRef.getSelection()

    // check if the cursor is going to go in next section
    if (inNextSection && !isShiftKey) {
      // if tab start from the beginning of section
      currentPosition = Number.MIN_SAFE_INTEGER
    } else if (inNextSection && isShiftKey) {
      // if shift tab start from the end of section
      currentPosition = Number.MAX_SAFE_INTEGER
    } else {
      currentPosition = currentPosition.index
    }

    // get all underscores(2 or more underscores) in current section
    const allUnderscoreIndexes = getUnderscoreOccurances(id)

    if (isEmpty(allUnderscoreIndexes) && isNil(currentPosition)) {
      return
    }

    if (!isShiftKey) {
      // if tab find first occurance of underscore
      let nextPos = allUnderscoreIndexes.find((elem) => {
        if (currentPosition < elem[0]) {
          return elem
        }
      })
      return nextPos
    }

    if (isShiftKey) {
      // if shift tab find last occurance of underscore
      let prevPos
      allUnderscoreIndexes.map((elem) => {
        if (currentPosition > elem[0]) {
          prevPos = elem
        }
      })
      return prevPos
    }
  }

  // Go through Delta fragment and insert contents into editor
  insertFragment = (fragment, insertionIdx) => {
    const ops = get(fragment, 'ops', [])

    ops.forEach((op, i) => {
      if ((i === ops.length - 1) && op.insert === '\n') {
        return
      }

      const newAttributes = {
        ...op.attributes,
        type: INLINE_TYPE.SCRIPT_CONTENT,
        bold: !!((op.attributes && op.attributes.bold)),
        italic: !!((op.attributes && op.attributes.italic))
      }

      this.quillRef.insertText(
        insertionIdx,
        op.insert,
        newAttributes
      )

      insertionIdx += op.insert.length
    })
  }

  componentDidUpdate = (prevProps, prevState) => {
    this.attachQuillRefs()

    // Insert content into editor. Currently used only for inserting scripts into Physician note view through the insert script
    // shortcut
    if (this.props.contentToInsert && !prevProps.contentToInsert) {
      const selection = this.quillRef.getSelection()

      if (selection) {
        let macroDelta
        try {
          macroDelta = convertS2ToDelta({ content: this.props.contentToInsert })
        } catch (err) {
          console.error('Error converting s2 to Delta', err)
          return
        }

        let insertionIdx = get(selection, 'index')

        this.insertFragment(macroDelta, insertionIdx)
        this.quillRef.deleteText(selection.index - 1, 1)
        this.props.clearContentToUpdate()
      }
    }

    if (
      (this.props.isSpeech && this.props.currentSectionId === this.props.id) &&
      (!prevProps.isSpeech || prevProps.currentSectionId !== prevProps.id) &&
      !this.props.isNewSection
    ) {
      const { value } = this.props
      const contentLen = JSON.parse(value).total_string_length
      const cp = this.props.startDictationCursor || contentLen
      this.quillRef.setSelection(cp, 0, 'api')
    }

    if (this.state.quillVal !== prevState.quillVal && this.state.isScriptInsert) {
      if (this.props.currentSectionId === this.props.id && this.props.cursorPosition !== undefined) {
        this.state.isScriptInsert = false;
        this.quillRef.setSelection(this.props.cursorPosition)
      } else {
        this.state.isScriptInsert = false;
        this.props.updateCursorTracking(this.props.id, this.props.cursorPosition)
      }
    }

    if (this.state.quillVal !== prevState.quillVal && this.props.isTranscription) {
      if (this.props.currentSectionId === this.props.id && this.props.cursorPosition !== undefined) {
        this.quillRef.setSelection(this.props.cursorPosition)
      } else {
        this.props.updateCursorTracking(this.props.id, this.props.cursorPosition)
      }
    }
  }

  componentWillReceiveProps = nextProps => {
    // if (nextProps.currentSectionId !== nextProps.id && this.props.currentSectionId === this.props.id) {
    //   console.log('blerp no longer in focus', nextProps.id)
    //   this.quillRef.setSelection(null)
    // }
    if (
      nextProps.readOnly && !this.props.readOnly &&
      nextProps.editorType !== 'SCRIPT' &&
      nextProps.editorType !== 'NOTETYPE' &&
      nextProps.persona === PERSONA.PHYSICIAN
    ) {
      this.setState({ readOnly: true })
      const el = document.getElementById(nextProps.id).getElementsByClassName('ql-editor')[0]
      el.style.cursor = 'default'
    }

    if (
      !nextProps.readOnly && this.props.readOnly &&
      nextProps.editorType !== 'NOTETYPE' &&
      nextProps.persona === PERSONA.PHYSICIAN
    ) {
      this.setState({ readOnly: false })
      const el = document.getElementById(nextProps.id).getElementsByClassName('ql-editor')[0]
      el.style.cursor = 'text'
    }

    // Don't blur if we are in the process of starting to type
    if (
      this.props.isTranscription && !nextProps.isTranscription && !nextProps.isWaitingForAudioProcessed
    ) {
      this.quillRef.blur()
    }

    if (!nextProps.isWaitingForAudioProcessed && this.props.isWaitingForAudioProcessed) {
      const selection = this.quillRef.getSelection()
      const cp = selection && selection.index
      const cToInsert = this.state.contentToInsert
      if (cp && cToInsert && cToInsert.length > 0) {
        this.quillRef.insertText(cp, this.state.contentToInsert, { type: 'TYPED_DOCTOR' })
      }
      this.setState({ contentToInsert: '' })
    }

    if (nextProps.value !== this.props.value || nextProps.force) {
      try {
        let newValue = nextProps.s2Mode
          ? convertS2ToDelta(nextProps.value)
          : convertSlateToDelta(nextProps.value || nextProps.defaultValue, nextProps.persona, nextProps.editorType)

        if (nextProps.readOnly && nextProps.cursorPosition !== undefined && nextProps.persona === 'ADMIN') {
          this.quillRef.setContents(newValue)
          this.quillRef.insertText(nextProps.cursorPosition, '●', { background: 'none', type: 'UNKNOWN', color: 'green' })
        } else {
          // Next content is parse-able so set error to false
          this.setState({
            quillVal: newValue,
            error: null
          })
        }
      } catch (err) {
        console.error('Unable to parse new Slate value', nextProps.value, err)
        this.setState({
          quillVal: ERROR_CONTENT,
          error: 'Unable to parse new Slate value'
        })
      }
    }

    // TODO: This is a no-op currently
    // If focus prop has changed and there is a slate editor, focus/blur the editor
    if (nextProps.focused !== this.props.focused) {
      this.quillRef.focus()
    }

    // When tab key is hit in keyboard mode, check if its not in the same section
    // then change section and select underscores for next section
    if (this.state.isTabKey && (this.props.currentSectionId !== nextProps.currentSectionId) && this.props.isMuted) {
      if (getQuillInstance(nextProps.currentSectionId)) {
        const nextQuillInstance = getQuillInstance(nextProps.currentSectionId)
        if (typeof nextQuillInstance.setSelection !== 'function') return

        // get all underscore positions in next section
        const allIndexes = this.getNextUnderscorePosition(nextProps.currentSectionId, this.state.isShiftKey, true)

        if (!isEmpty(allIndexes)) {
          const [firstIndex, lastIndex] = allIndexes
          nextQuillInstance.setSelection(firstIndex, lastIndex - firstIndex)
        } else {
          // if next section is empty set cursor position at the beginning
          if (this.isQuillEmpty(nextQuillInstance)) {
            nextQuillInstance.setSelection(0, 0, 'api')
          } else {
            // if next section is not empty and doesn't have any underscores then set cursor position at the end
            nextQuillInstance.setSelection(nextQuillInstance.getLength(), 0, 'api')
          }
        }
      }
      this.setState({ isTabKey: false, isShiftKey: false, currentUnderscorePositions: null })
    }
  }

  handleClick = e => {
    if (this.props.enablePunct && e.altKey) {
      this.punctuate()
    }
  }

  // rewrite for quill
  punctuate = () => {
    const selection = this.quillRef.getSelection()

    const textAfterSelection = this.quillRef.getText(selection.index)
    const indexOfPeriod = textAfterSelection.indexOf('.')
    // Period should go before either space or newline
    const indexOfSpace = textAfterSelection.indexOf(' ')
    const indexOfNewLine = textAfterSelection.indexOf('\n')

    let indexToInsertPeriod = -1

    if (indexOfSpace === -1) {
      // indexOfNewLine never -1 because last character in quill is always \n
      indexToInsertPeriod = indexOfNewLine
    } else {
      indexToInsertPeriod = Math.min(indexOfSpace, indexOfNewLine)
    }

    if (indexOfPeriod === -1 || indexToInsertPeriod < indexOfPeriod) {
      const spaceOrNewLine = textAfterSelection.charAt(indexToInsertPeriod)

      const capitalNextLetter = textAfterSelection
        .charAt(indexToInsertPeriod + 1)
        .toUpperCase()

      this.quillRef.deleteText(
        selection.index + indexToInsertPeriod,
        2
      )

      this.quillRef.insertText(
        selection.index + indexToInsertPeriod,
        `.${spaceOrNewLine}${capitalNextLetter}`,
        { type: getType(this.props.persona, this.props.editorType) }
      )
    } else if (indexOfPeriod < indexToInsertPeriod) {
      const lowercaseNextLetter = textAfterSelection
        .charAt(indexOfPeriod + 2)
        .toLowerCase()

      this.quillRef.deleteText(
        selection.index + indexOfPeriod + 2,
        1
      )

      this.quillRef.insertText(
        selection.index + indexOfPeriod + 2,
        lowercaseNextLetter,
        { type: getType(this.props.persona, this.props.editorType) }
      )

      this.quillRef.deleteText(
        selection.index + indexOfPeriod,
        1
      )
    }
  }

  insertScript = () => {
    const { getMacroToPaste, persona, editorType } = this.props
    if (getMacroToPaste) {
      const macro = getMacroToPaste()
      if (!macro || !macro.content) return
      let macroDelta = null
      try {
        macroDelta = convertSlateToDelta(macro.content, persona, editorType)
      } catch (err) {
        console.error('Error parsing slate in script', err)
        return
      }

      let insertionIdx = get(this.quillRef.getSelection(), 'index')
      this.insertFragment(macroDelta, insertionIdx)
    }
  }

  isCopyOrCut = e => {
    if (e.ctrlKey || e.metaKey) {
      const keyCode = e.keyCode
      if (keyCode === KEY_CODE.C || keyCode === KEY_CODE.X) {
        return true
      }
    }

    return false
  }

  handleKeyDown = (e) => {
    const {
      persona,
      editorType,
      exitSpeechEnterTyping,
      isMuted,
      isWaitingForAudioProcessed,
      handleTabKeyPress,
      currentSectionId
    } = this.props

    const key = e.key
    const keyCode = e.keyCode

    // Prevent edits if waiting on S2 audio_processed
    // TODO: name this variable something more generic
    if (isWaitingForAudioProcessed) {
      e.preventDefault()
      return
    }

    // If key is pressed in physician note view
    if (
      persona === PERSONA.PHYSICIAN &&
      !isMuted &&
      (isKeyCodePrintable(keyCode) || key === 'Backspace') &&
      editorType !== 'SCRIPT' && editorType !== 'NOTETYPE'
    ) {
      e.preventDefault()

      // Save content to be inserted once audio_processed is received
      if (key !== 'Backspace' && key !== 'Enter' && key !== 'Tab') {
        this.setState({ contentToInsert: key })
      }

      exitSpeechEnterTyping(key)
      return
    }

    // If Tab key is pressed in physcian note view in dictation mode
    if (persona === PERSONA.PHYSICIAN && key === 'Tab' &&
      editorType !== 'SCRIPT' && editorType !== 'NOTETYPE' && !isMuted) {
      e.preventDefault()
      handleTabKeyPress(e.shiftKey)
      return
    }

    // If Tab key is pressed in physcian note view in dictation mode
    if (persona === PERSONA.PHYSICIAN && key === 'Tab' &&
      editorType !== 'SCRIPT' && editorType !== 'NOTETYPE' && isMuted) {
      e.preventDefault()

      // get all underscores(wild card for 2 or more) for the current section
      const nextPositions = this.getNextUnderscorePosition(currentSectionId, e.shiftKey)

      // check if underscores are present is current section
      // if yes stay in current section
      if (isEmpty(nextPositions)) {
        handleTabKeyPress(e.shiftKey)
      } else {
        const [nextIndexStart, nextIndexEnd] = nextPositions
        this.quillRef.setSelection(nextIndexStart, nextIndexEnd - nextIndexStart)
      }

      // set states to track current section
      this.setState({ isTabKey: true, isShiftKey: e.shiftKey, currentUnderscorePositions: nextPositions })
      return
    }

    // TODO: i'm unable to get this working
    // if (keyCode === 27) {
    //   console.log('this.reactQuillRef', this.reactQuillRef)
    //   console.log('pressed escape key')
    //   // this.quillRef.setSelection(null)
    //   // this.reactQuillRef && this.reactQuillRef.blur()
    // }

    // This is for ops script insertion shortcut
    if (e.ctrlKey && key === KEY.M) {
      e.preventDefault()
      this.insertScript()
    }

    // TODO: disregard ALL inputs that don't add a character to the editor
    const isCopyOrCut = this.isCopyOrCut(e)
    if (isCopyOrCut) {
      return
    }

    // Special case handling for ops audio pause shortcut - eventually
    // should move these shortcuts to the Quill editor itself
    if (e.shiftKey && key === KEY.SPACE) {
      return
    }

    // Only apply formatting to characters that will be printed - aka increases length of content.
    const inputType = getType(persona, editorType)
    const currentFormatType = get(this.quillRef.getFormat(), 'type')
    if (
      isKeyCodePrintable(keyCode) &&
      currentFormatType !== inputType
    ) {
      this.quillRef.format('type', inputType)
    }
  }

  handleChange = (content, change, source, editor) => {
    const { metadata } = this.state
    const { editorType, persona } = this.props

    let cp = get(editor.getSelection(true), 'index')

    // When pasting, the cursor position returned from editor.getSelection() is for some reason not up
    // to date.
    if (didPaste) {
      didPaste = false
      cp += change.changeLength()
    }

    const removeDenotationChar = ops => {
      return ops.map((n, i, a) => {
        const nextIsScript = a[i + 1]?.attributes?.type === 'script_content'
        if(nextIsScript && n.attributes?.type === 'doctor_typed') {
          n.insert = n.insert.replace(/\+$/, '')
        }
        return n;
      }).filter(n => n.insert)
    }

    const editorContentsOrig = editor.getContents()
    const editorContents = {...editorContentsOrig, ops: removeDenotationChar(editorContentsOrig.ops)}

    const slateToSave = this.props.s2Mode
      ? convertDeltaToS2(editorContents, persona)
      : convertDeltaToSlate(editorContentsOrig, editor.getLength(), metadata, editorType)

    this.props.onChange(JSON.stringify(slateToSave), cp, editor.getText())
  }

  handlePaste = e => {
    didPaste = true
  }

  // Weird workaround - setting the custom bindings in realtime causes weird out-of-range errors.
  // Caching it here prevents that issue.
  modules = ({
    history: {},
    toolbar: false,
    keyboard: {
      bindings: {
        // Tab was replaced by key stroke('\t') in editor
        // Return true to let later handlers be called
        tab: {
          key: 9,
          handler: () => true
        },
        custom: {
          key: 13,
          handler: (range, context) => {
            const selection = this.quillRef.getSelection()
            this.quillRef.insertText(
              selection.index,
              '\n',
              { 'blockType': getType(this.props.persona, this.props.editorType) }
            )
          }
        }
      }
    }
  })

  // Render the editor.
  render() {
    const {
      id,
      name,
      readOnly,
      onError,
      style = {},
      persona,
      editorType,
      cursorPosition,
      placeholder,
      enableChips,
      handleUnidecode,
      enableUnidecoding,
      getMacroContent
    } = this.props

    const {
      quillVal,
      defaultQuillVal,
      error
    } = this.state

    if (error) {
      onError && onError()
    }

    const editorProps = {}
    const isControlled = !!quillVal
    if (isControlled) {
      if (readOnly && cursorPosition !== undefined && persona !== PERSONA.PHYSICIAN) {
        editorProps.value = this.quillRef && this.quillRef.getContents()
      } else {
        editorProps.value = quillVal
      }
    } else {
      editorProps.defaultValue = defaultQuillVal
    }

    let className = persona
    if (editorType === EDITOR_TYPE.DETAILED) {
      className = EDITOR_TYPE.DETAILED
    }
    if (editorType === EDITOR_TYPE.SCRIPT && !readOnly) {
      className = className + ' edit-script'
    }

    if (enableChips) {
      // https://github.com/afry/quill-mention
      this.modules.mention = {
        allowedChars: /^[A-Za-z\sÅÄÖåäö]*$/,
        mentionDenotationChars: ['+'],
        dataAttributes: ['cy'],
        source: function (searchTerm, renderList, mentionChar) {
          let values = plusValues
          if (persona === PERSONA.ADMIN) {
            values = [...values, ...adminOnlyPlusValues]
          }
          values = values.map((vital, i) => {
            const vitalName = vital.value.replace(/\s+/g, '-').toLowerCase()
            return { ...vital, cy: `vitals-list-${vitalName}` }
          })
          if (searchTerm.length === 0) {
            renderList(values, searchTerm)
          } else {
            const matches = []
            for (let i = 0; i < values.length; i++) {
              if (
                ~values[i].value.toLowerCase().indexOf(searchTerm.toLowerCase())
              ) { matches.push(values[i]) }
              renderList(matches, searchTerm)
            }
          }
        }
      }
    } else if (persona === PERSONA.PHYSICIAN) {
      this.modules.mention = {
        allowedChars: /^[A-Za-z\sÅÄÖåäö]*$/,
        mentionDenotationChars: ['+'],
        ...(isAmwellTheme() && {
          mentionContainerClass: 'ql-mention-list-container-amwell',
          mentionListClass: 'ql-mention-list-amwell',
          listItemClass: 'ql-mention-list-item-amwell'
        }),
        dataAttributes: ['cy'],
        positioningStrategy: 'fixed',
        source: (searchTerm, renderList, mentionChar) => {
          const { scripts } = this.props
          if (!scripts) return

          const scriptValues = scripts && scripts.map((s, i) => {
            const sectionName = name || 'new'
            const scriptName = s.tags[0].name.value
            return {
              cy: `section-${sectionName.replace(/\s/g, '_')}-script-${scriptName.replace(/\s/g, '_')}`,
              id: s.id,
              value: scriptName
            }
          })
          let values = scriptValues
          if (searchTerm.length === 0) {
            renderList(values, searchTerm)
          } else {
            const matches = []
            for (let i = 0; i < values.length; i++) {
              if (~values[i].value.toLowerCase().indexOf(searchTerm.toLowerCase())) {
                matches.push(values[i])
              }
              renderList(matches, searchTerm)
            }
          }
        },
        onSelect: async ({ id }) => {
          this.state.isScriptInsert = true;
          const { id: currentSectionId }  = this.props;
          console.log("QuillEditor.render.mention.onSelect;\nscriptId: %o\nthe id of the current section(this.props.id): %o\nthis.props.currentSectionId: %o", id, currentSectionId, this.props.currentSectionId)
          await this.props.clickInsertScript(id, currentSectionId)
        }
      }
    }

    if (enableUnidecoding) {
      this.modules.clipboard = {
        matchers: [
          [NODE_TYPES.TEXT_NODE, function (node, delta) {
            // bubble up to parent if unidecode done
            const isUnidecodingRequired = unidecode(node.data) !== node.data
            let text = node.data

            if (isUnidecodingRequired) {
              text = unidecode(node.data)

              handleUnidecode({
                beforeText: node.data,
                afterText: text
              })
            }

            return new Delta().insert(text)
          }]
        ]
      }
    }

    return (
      <div
        onClick={this.handleClick}
        id={id}
        className={className}
        onPaste={this.handlePaste}
        data-cy={this.props["data-cy"]}
      >
        <ReactQuill
          id={`sectionEditor-${id}`}
          onChange={readOnly ? () => { } : this.handleChange}
          theme={'snow'} // default Quill theme
          onKeyDown={(e) => this.handleKeyDown(e)}
          ref={ref => { this.reactQuillRef = ref }}
          readOnly={readOnly || error}
          modules={this.modules}
          formats={FORMATS}
          {...editorProps}
          style={style}
          placeholder={placeholder}
        />
      </div>
    )
  }
}

export { V2_DEFAULT_CONTENT }
export default QuillEditor
